import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Container,
    Grid,
    Chip,
    TextField,
    MenuItem,
    Stack
} from "@mui/material";
import {
    TrendingUp,
    Download,
    FilterList,
    AccountBalanceWallet
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import creditService from "../../services/creditService";

export default function CreditHistoryPage() {
    const { userProfile } = useAuth();
    const [creditHistory, setCreditHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState("all");
    const [searchPath, setSearchPath] = useState("");

    useEffect(() => {
        const fetchCreditHistory = async () => {
            try {
                setLoading(true);

                if (!userProfile?.uid) return;

                const credits = await creditService.getUserCredits(userProfile.uid);
                setCreditHistory(credits || []);
            } catch (error) {
                console.error("Error fetching credit history:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCreditHistory();
    }, [userProfile]);

    const getFilteredHistory = () => {
        return creditHistory.filter((item) => {
            const itemType = item.activityType || "Other";
            const typeMatch = filterType === "all" || filterType === itemType;
            const pathMatch = `${item.activityType || ""} ${item.description || ""}`
                .toLowerCase()
                .includes(searchPath.toLowerCase());

            return typeMatch && pathMatch;
        });
    };

    const getTypeLabel = (type = "") => {
        return String(type)
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getTypeIcon = (amount) => {
        return Number(amount || 0) >= 0 ? "▲" : "▼";
    };

    const positiveCount = creditHistory.filter((item) => Number(item.amount || 0) > 0).length;

    const negativeCount = creditHistory.filter((item) => Number(item.amount || 0) < 0).length;

    const filteredHistory = getFilteredHistory();

    const downloadHistory = () => {
        const headers = [
            "Date",
            "Activity Type",
            "Description",
            "Type",
            "Credit Change",
            "Status",
            "Reference"
        ];

        const rows = filteredHistory.map((item) => [
            new Date(item.createdAt).toLocaleDateString(),
            getTypeLabel(item.activityType),
            item.description || "-",
            item.metadata?.source || "manual",
            Number(item.amount || 0),
            item.status || "approved",
            item.metadata?.activityId || "-"
        ]);

        const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `credit-history-${new Date().toISOString().split("T")[0]}.csv`;
        a.click();
    };

    if (loading) {
        return (
            <AdminLayout title="Credit History">
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "400px"
                    }}
                >
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Credit History">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "#003366" }}>
                        💎 Credit History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Track all your earned credits from learning paths and level completions
                    </Typography>
                </Box>

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #003366 0%, #0A2342 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <AccountBalanceWallet sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {Number(userProfile?.totalCredits || 0)}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Current Balance
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #C1272D 0%, #8B1E3F 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    🎖️
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {positiveCount}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Credit Increases
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #0A2342 0%, #003366 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                    ✅
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {negativeCount}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Credit Deductions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                background: "linear-gradient(135deg, #003366 0%, #C1272D 100%)",
                                color: "white",
                                borderRadius: 2
                            }}
                        >
                            <CardContent sx={{ textAlign: "center" }}>
                                <TrendingUp sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    {creditHistory.length}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Total Transactions
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters */}
                <Card sx={{ mb: 3, p: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField
                            size="small"
                            placeholder="Search by activity or description..."
                            value={searchPath}
                            onChange={(e) => setSearchPath(e.target.value)}
                            sx={{ flex: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <FilterList sx={{ mr: 1, color: "text.secondary" }} />
                                )
                            }}
                        />
                        <TextField
                            size="small"
                            select
                            label="Filter by Type"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="all">All Types</MenuItem>
                            {[...new Set(creditHistory.map((c) => c.activityType).filter(Boolean))].map((type) => (
                                <MenuItem key={type} value={type}>{getTypeLabel(type)}</MenuItem>
                            ))}
                        </TextField>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center"
                            }}
                        >
                            <Chip
                                icon={<Download />}
                                label="Export CSV"
                                onClick={downloadHistory}
                                color="primary"
                                variant="outlined"
                            />
                        </Box>
                    </Stack>
                </Card>

                {/* History Table */}
                {filteredHistory.length === 0 ? (
                    <Alert severity="info" sx={{ textAlign: "center", py: 4 }}>
                        <Typography>
                            {creditHistory.length === 0
                                ? "No credit history yet. Complete learning paths to earn credits!"
                                : "No results match your filters. Try adjusting your search."}
                        </Typography>
                    </Alert>
                ) : (
                    <Card>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: "primary.lighter" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Activity Type</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Change Source</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Status
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Credit Change
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Reference
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredHistory.map((item, index) => (
                                        <TableRow
                                            key={item.id}
                                            hover
                                            sx={{
                                                backgroundColor: Number(item.amount || 0) >= 0 ? "rgba(0,51,102,0.03)" : "rgba(193,39,45,0.04)",
                                                "&:hover": {
                                                    backgroundColor: Number(item.amount || 0) >= 0 ? "rgba(0,51,102,0.08)" : "rgba(193,39,45,0.10)"
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                {new Date(item.createdAt).toLocaleDateString("en-IN", {
                                                    year: "numeric",
                                                    month: "short",
                                                    day: "numeric",
                                                    hour: "2-digit",
                                                    minute: "2-digit"
                                                })}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                {getTypeLabel(item.activityType)}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {item.description || "-"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={item.metadata?.source || "manual"} variant="outlined" />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    icon={
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ fontSize: "16px !important" }}
                                                        >
                                                            {getTypeIcon(item.amount)}
                                                        </Typography>
                                                    }
                                                    label={(item.status || "approved").toUpperCase()}
                                                    size="small"
                                                    color={item.status === "approved" ? "success" : "warning"}
                                                    variant="filled"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color:
                                                            Number(item.amount || 0) >= 0 ? "#003366" : "#C1272D"
                                                    }}
                                                >
                                                    {Number(item.amount || 0) > 0 ? `+${item.amount}` : String(item.amount || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {item.metadata?.activityId || item.id}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Summary Footer */}
                        <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    Showing {filteredHistory.length} of {creditHistory.length} transactions
                                </Typography>
                                <Box sx={{ display: "flex", gap: 3 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Credits in this view
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                color: "success.main"
                                            }}
                                        >
                                            {filteredHistory.reduce((sum, item) => sum + Number(item.amount || 0), 0)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </Card>
                )}

                {/* Legend */}
                <Card sx={{ mt: 3, p: 2, background: "linear-gradient(135deg, rgba(0,51,102,0.06), rgba(193,39,45,0.06))", border: "1px solid rgba(0,51,102,0.2)" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        📌 Credit Updates Overview
                    </Typography>
                    <Stack spacing={1}>
                        <Typography variant="caption">
                            <strong>Credit Change:</strong> Positive values indicate credits added, negative values indicate deductions.
                        </Typography>
                        <Typography variant="caption">
                            <strong>Status:</strong> Approved means the update is finalized in your credit balance.
                        </Typography>
                        <Typography variant="caption">
                            <strong>Reference:</strong> Tracks source activity or transaction ID for each update.
                        </Typography>
                    </Stack>
                </Card>
            </Container>
        </AdminLayout>
    );
}
